import { injectable } from '~/container'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { GuestBooking, GuestBookingBooking, bookingSchema } from './schema'

@injectable()
export class GuestBookingRepositoryFetch {
  async getByToken(token: string, langCode: string): Promise<GuestBooking> {
    const result: GuestBookingFetchResult =
      await $fetch<GuestBookingFetchResult>(this.getUrl(token, langCode), {
        method: 'GET',
      })
    if (result.userBooking) {
      const booking = this.mapResponse(result.userBooking, token)
      return { error: result.error, booking }
    }
    return {
      error: result.error,
      booking: result.userBooking,
      reviewId: result.reviewId,
    }
  }

  private getUrl(token: string, langCode: string) {
    return getLangFallbackUrl(langCode, `/_/endpoints/guest-booking/${token}/`)
  }

  private mapResponse(
    userBooking: GuestBookingResponse,
    token: string,
  ): GuestBookingBooking {
    const result = bookingSchema.parse({
      ...userBooking,
      token,
    })
    const categories = userBooking.pitchtype?.categories || undefined
    if (result && categories) {
      result.categories = categories.map((c) => c.id)
    }
    const primaryPhoto = userBooking.pitchtype?.primaryPhoto || undefined
    if (result && primaryPhoto) {
      result.primaryPhoto = {
        url: { masterImage: primaryPhoto.url.master_image },
      }
    }
    return result
  }
}

interface GuestBookingResponse {
  arrive: string
  depart: string
  prettyId: string
  status: number
  isBYO: boolean
  unitType: {
    id: string
    name: string
    slug: string
  } | null
  partyDetailed: {
    adults: number
    children: number
    childrenAges: number[]
    infants: number
    infantAges: number[]
    dogs: number
    display: string
  }
  pitchtype?: {
    name: string
    categories: Array<{ id: string }>
    primaryPhoto?: {
      url: {
        master_image: string
        galleryLarge: string
        galleryMedium: string
        gallerySmall: string
        userPhoto: string
      }
    }
  }
  campsite?: {
    id: number
    slug: string
    rating: number
    rateCount: number
    name: string
    fullAddress: string
    primaryPhoto: {
      url: {
        master_image: string
        galleryLarge: string
        galleryMedium: string
        gallerySmall: string
        userPhoto: string
      }
    }
    path: string
  }
}

interface GuestBookingFetchResult {
  userBooking?: GuestBookingResponse
  error: string
  reviewId?: string
}
